import { useEffect, useState } from "react";
import { usePatchModel } from "../api/models";
import type { ModelDetail } from "../api/types";
import { categoryWord } from "../assets/categories";
import { ErrorLine } from "../ui/ErrorLine";
import { Field, Input } from "../ui";
import { type Vec3, sizeCm } from "../viewer/math";
import { SIZE_ERROR, metresOf } from "./NewDesignPage";

/** The stored real size as the field shows it: centimetres, or empty. */
function cmText(metres: number | null): string {
  return metres === null ? "" : String(Math.round(metres * 1e4) / 100);
}

/** "View code": the design's code as text, fetched only when opened. It is
 *  put in the page as text, never as markup. */
function Code({ url }: { url: string }) {
  const [state, setState] = useState<
    | { kind: "closed" }
    | { kind: "loading" }
    | { kind: "text"; text: string }
    | { kind: "failed" }
  >({ kind: "closed" });

  const load = () => {
    if (state.kind === "loading" || state.kind === "text") return;
    setState({ kind: "loading" });
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("not ok");
        return response.text();
      })
      .then((text) => setState({ kind: "text", text }))
      .catch(() => setState({ kind: "failed" }));
  };

  return (
    <details
      className="text-sm text-slate-700"
      data-testid="view-code"
      onToggle={(event) => {
        if (event.currentTarget.open) load();
      }}
    >
      <summary className="min-h-[44px] cursor-pointer py-2 font-semibold md:min-h-0 md:py-0">
        View code
      </summary>
      {state.kind === "loading" ? (
        <p role="status" className="m-0 mt-2">
          Loading…
        </p>
      ) : state.kind === "failed" ? (
        <p role="alert" className="m-0 mt-2">
          The code could not be loaded. Close this and open it again.
        </p>
      ) : state.kind === "text" ? (
        <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-slate-900 p-3 text-xs leading-5 text-slate-100">
          {state.text}
        </pre>
      ) : null}
    </details>
  );
}

/** "Details": what the design is, how big it came out, the real size the
 *  user states, and its code behind a disclosure ([design-studio §11.8]). */
export function DetailsPanel({ detail }: { detail: ModelDetail }) {
  const patch = usePatchModel(detail.id);
  const stored = cmText(detail.real_size_m);
  const [size, setSize] = useState(stored);
  useEffect(() => setSize(stored), [stored]);
  const [wrong, setWrong] = useState(false);

  const current = detail.current;
  const meta = current?.asset.meta ?? {};
  const description = String(meta.description ?? "").trim();
  const category = typeof meta.category === "string" ? meta.category : "";
  const tags: string[] = Array.isArray(meta.tags) ? meta.tags : [];
  const sizeM = current?.report.size_m;
  const triangles = current?.report.triangles;

  const save = () => {
    const metres = metresOf(size);
    if (Number.isNaN(metres)) {
      setWrong(true);
      return;
    }
    setWrong(false);
    if (metres === detail.real_size_m) return;
    patch.mutate(
      { real_size_m: metres },
      // An edited value returns to what it was.
      { onError: () => setSize(stored) },
    );
  };

  return (
    <section className="flex flex-col gap-3" data-testid="details-panel">
      <p className="m-0 text-sm font-semibold text-slate-800">{detail.name}</p>
      {description ? (
        <p className="m-0 text-sm text-slate-700">{description}</p>
      ) : null}
      {category ? (
        <p className="m-0 text-sm text-slate-600">{categoryWord(category)}</p>
      ) : null}
      {tags.length > 0 ? (
        <ul
          className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-sm text-slate-600"
          aria-label="Tags"
        >
          {tags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
      ) : null}
      {Array.isArray(sizeM) ? (
        <p className="m-0 text-sm text-slate-700">
          Size: {sizeCm(sizeM as Vec3)}
        </p>
      ) : null}
      {typeof triangles === "number" ? (
        <p className="m-0 text-sm text-slate-700">
          Detail: {triangles.toLocaleString("en-US")} triangles
        </p>
      ) : null}
      <Field label="Longest side (cm)" error={wrong ? SIZE_ERROR : undefined}>
        <Input
          type="number"
          inputMode="decimal"
          min={0.5}
          max={5000}
          step="any"
          value={size}
          invalid={wrong}
          disabled={patch.isPending}
          aria-busy={patch.isPending ? true : undefined}
          onChange={(event) => setSize(event.target.value)}
          onBlur={save}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
      </Field>
      {patch.isError ? (
        <ErrorLine error={patch.error} context="design" />
      ) : null}
      {detail.code_url ? <Code url={detail.code_url} /> : null}
    </section>
  );
}
